import { z } from 'zod'

import type { Access } from '@/shared/access'
import { auditShape } from '@/shared/details'
import { recordPath, recordRoute } from '@/shared/registry'

/** Where the class pages live. */
export const createClassPath = '/classes/classes/new'
export const classRoute = recordRoute('classes', 'classes')
export const editClassRoute = `${classRoute}/edit`

/** A class's details page. */
export function classPath(id: string): string {
  return recordPath('classes', 'classes', id)
}

/** A class's edit page. */
export function editClassPath(id: string): string {
  return `${classPath(id)}/edit`
}

/** The query parameter that presets the owning organization on Create Class. */
export const organizationParameter = 'organization'

/** The query parameters that open Create Class filled in from a template (Open Templates) or another class (Clone Event). */
export const templateParameter = 'template'
export const cloneParameter = 'clone'

/** Create Class filled in from a template. */
export function fromTemplatePath(id: string): string {
  return `${createClassPath}?${new URLSearchParams({ [templateParameter]: id }).toString()}`
}

/** Create Class filled in from another class (Clone Event). */
export function clonePath(id: string): string {
  return `${createClassPath}?${new URLSearchParams({ [cloneParameter]: id }).toString()}`
}

/** The base of Class Details' slots (`detailsSlots`). */
export const classDetailsSlot = 'classes.class-details'

/** What each class action needs; a status other than the default, going over the maximum and visibility to all need their aspect. */
export const readClassesPermission = 'classes.classes.read'
export const writeClassesPermission = 'classes.classes.write'
export const deleteClassesPermission = 'classes.classes.delete'
export const writeClassStatusPermission = 'classes.classes.status.write'
export const writeClassVisibilityAllPermission = 'classes.classes.visibility-all.write'

/**
 * The scopes that make someone a classes administrator. Creating a class needs write at one, and the
 * admin class searches read at one: a grant held only at a relationship, as every built-in role holds
 * for editing delegates, opens neither.
 */
export const organizationScopes: readonly string[] = ['managedOrganizations', 'allOrganizations']

/**
 * The scopes at which classes read opens a class from another area's link: all but the editing
 * delegate one, which every built-in role holds and which reaches only the classes that list the person.
 */
export const classLinkScopes: readonly string[] = [...organizationScopes, 'instructs', 'created']

/** Whether another area shows a class as a link to it: classes read that reaches every class, or held at one of `classLinkScopes`. */
export function mayLinkToClasses(access: Pick<Access, 'can' | 'reachesEverything'>): boolean {
  return access.reachesEverything(readClassesPermission) || access.can(readClassesPermission, classLinkScopes)
}

/** The API's class endpoints. */
export const classesApi = '/classes/v1/classes'

export function classApi(id: string): string {
  return `${classesApi}/${encodeURIComponent(id)}`
}

export function classStatusApi(id: string): string {
  return `${classApi(id)}/status`
}

/** Creates a class from a template or another class, which it takes what the form does not hold from. */
export function classCopyApi(id: string): string {
  return `${classApi(id)}/copy`
}

/** What a new class of the organization, and of the course once chosen, starts from. */
export function classDefaultsApi(organizationId: string, courseId: string | null): string {
  const query = new URLSearchParams({ organizationId })
  if (courseId !== null) {
    query.set('courseId', courseId)
  }
  return `${classesApi}/defaults?${query.toString()}`
}

/** Legacy's eight statuses, in its order. */
export const classStatuses = ['scheduled', 'guaranteedToRun', 'canceled', 'rescheduled', 'tentative', 'requested', 'denied', 'completed'] as const
export type ClassStatus = (typeof classStatuses)[number]

export const deliveryTypes = ['physical', 'virtual', 'both'] as const
export type DeliveryType = (typeof deliveryTypes)[number]

/** The mockups' status badge tones, for all eight statuses. */
export const statusTones: Readonly<Record<ClassStatus, 'neutral' | 'primary' | 'success' | 'error'>> = {
  scheduled: 'primary',
  guaranteedToRun: 'success',
  canceled: 'error',
  rescheduled: 'primary',
  tentative: 'neutral',
  requested: 'neutral',
  denied: 'error',
  completed: 'success',
}

/** Where a session's WebEx meeting stands, on a class with a WebEx connection (CLS-08). */
export const meetingStates = ['none', 'pending', 'ready', 'failed', 'notSetUp', 'abandoned'] as const
export type MeetingState = (typeof meetingStates)[number]

const sessionSchema = z.object({
  id: z.string(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  meetingState: z.enum(meetingStates).nullish(),
})

/** A session: its day and times in the class's time zone, when it starts and ends in UTC, and its meeting's state. */
export type ClassSession = z.infer<typeof sessionSchema>

const classSchema = z
  .object({
    id: z.string(),
    organizationId: z.string(),
    organizationName: z.string(),
    courseId: z.string(),
    courseName: z.string(),
    publishingGroupId: z.string().nullish(),
    name: z.string(),
    htmlDescription: z.string().nullish(),
    status: z.enum(classStatuses),
    timeZone: z.string(),
    deliveryLanguage: z.string().nullish(),
    minimumStudents: z.number().int(),
    maximumStudents: z.number().int(),
    deliveryType: z.enum(deliveryTypes),
    classrooms: z.array(z.object({ id: z.string(), name: z.string(), isPrimary: z.boolean() })),
    customUrl: z.string().nullish(),
    sendsNameAndEmailToCustomUrl: z.boolean(),
    webExConnectionId: z.string().nullish(),
    webExConnectionName: z.string().nullish(),
    companyId: z.string().nullish(),
    companyName: z.string().nullish(),
    externalId: z.string().nullish(),
    isUniqueDelivery: z.boolean(),
    allowsSelfEnrollment: z.boolean(),
    anyoneMaySelfEnroll: z.boolean(),
    allowsSelfEnrollmentWhileInProgress: z.boolean(),
    visibleToAllOrganizations: z.boolean(),
    visibleToOrganizations: z.array(z.object({ id: z.string(), name: z.string() })),
    showsOnPublicSchedule: z.boolean(),
    allowsAnonymousDetails: z.boolean(),
    isTemplate: z.boolean().nullish(),
    startsOn: z.string(),
    endsOn: z.string(),
    sessions: z.array(sessionSchema),
    courseArchivesOn: z.string().nullish(),
  })
  .extend(auditShape)

/** A class as Class Details and Edit Class show it. */
export type ClassRecord = z.infer<typeof classSchema>

export function parseClass(body: unknown): ClassRecord {
  return classSchema.parse(body)
}

const defaultsSchema = z.object({
  minimumStudents: z.number().int(),
  maximumStudents: z.number().int(),
  absoluteMaximumStudents: z.number().int().nullish(),
  status: z.enum(classStatuses),
  timeZone: z.string(),
  sessionDays: z.array(z.string()),
  sessionStartTime: z.string(),
  sessionEndTime: z.string(),
  sessionCount: z.number().int(),
  allowsSelfEnrollment: z.boolean(),
})

/** What Create Class starts from. */
export type ClassDefaults = z.infer<typeof defaultsSchema>

export function parseClassDefaults(body: unknown): ClassDefaults {
  return defaultsSchema.parse(body)
}
